/** @jsxImportSource @emotion/react */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useFacilityLocations } from "../../../../hooks/useFacilityLocationQueries";
import { getFilledArrayOrDefault } from "../../../../utils/getFilledArrayOrDefault";
import { toast } from "../../../../utils/toast";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import MetaForm from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";

interface ScheduleOption {
    value: string;
    label: string;
}

interface RipetapSettings {
    ripetap_schedule?: string;
    number_of_taps?: number;
    interval_in_seconds?: number;
    facility_location_id?: string;
    UID: string;
}

const scheduleOptions: ScheduleOption[] = [
    { value: "every_15_minutes", label: "Every 15 minutes" },
    { value: "every_30_minutes", label: "Every 30 minutes" },
    { value: "every_1_hour", label: "Every 1 hour" },
    { value: "every_2_hours", label: "Every 2 hours" },
    { value: "every_3_hours", label: "Every 3 hours" },
    { value: "every_4_hours", label: "Every 4 hours" },
    { value: "every_6_hours", label: "Every 6 hours" },
    { value: "every_8_hours", label: "Every 8 hours" },
    { value: "every_16_hours", label: "Every 16 hours" },
];

export default function RipetapScheduleSettings() {
    const config = useConfig();
    const device = useSelector((state:any) => state.devices.current);
    const { data: locations } = useFacilityLocations({ offset: 0, limit: 10000 });
    const queryClient = useQueryClient();
    const [query, setQuery] = useState<RipetapSettings>({ ...(device?.config || {}), UID: device?.UID });

    useEffect(() => {
        setQuery({ ...(device?.config || {}), UID: device?.UID, facility_location_id: device?.facility_location_id });
    }, [device?.UID]);

    const updateRipetapScheduleMutation = useMutation({
        mutationFn: async (data: RipetapSettings) => {
            return callAvosApi(`/device/management/${data.UID}/`, {
                method: "PUT",
                data: {
                    config: {
                        ripetap_schedule: data.ripetap_schedule,
                        number_of_taps: data.number_of_taps || 1,
                        interval_in_seconds: data.interval_in_seconds || 15,
                    },
                    facility_location_id: data.facility_location_id,
                },
            });
        },
        onSuccess: () => {
            toast.success("Ripetap schedule updated successfully!");
            // Invalidate device queries to refresh the data
            queryClient.invalidateQueries({ queryKey: ["device", device.UID] });
        },
        onError: (error: any) => {
            // eslint-disable-next-line no-console
            console.error("Error updating ripetap schedule:", error);
            toast.error("Failed to update ripetap schedule. Please try again.");
        },
    });

    const form = [
        {
            label: "Ripetap Schedule",
            name: "ripetap_schedule",
            type: "single-select",
            description: "The schedule to use.",
            options: scheduleOptions,
        },
        {
            label: "Number of Taps",
            name: "number_of_taps",
            default_value: 1,
            description: "The number of taps to perform.",
            type: "number",
        },
        {
            label: "Interval in Seconds",
            name: "interval_in_seconds",
            default_value: 10,
            description: "The interval in seconds between each tap. Default is 10 seconds.",
            type: "number",
        },
        {
            label: "Ripening Room",
            name: "facility_location_id",
            type: "single-select",
            description: "Where is this tapper located?",
            options: getFilledArrayOrDefault(locations).map((i) => ({ label: i.label, value: i.location_id })),
        }
    ];

    const handleSave = () => updateRipetapScheduleMutation.mutate(query);

    return (
        <div>
            <MetaForm
                meta={form}
                setValue={(field: string, value: any) => setQuery((prev) => ({ ...prev, [field]: value }))}
                object={query}
                config={config}
            />
            <Button
                color="primary"
                onClick={handleSave}
                disabled={updateRipetapScheduleMutation.isPending}
            >
                {updateRipetapScheduleMutation.isPending ? "Saving..." : "Save"}
            </Button>
        </div>
    );
}
